import { getDomain } from '@/helpers/domainPartners';
interface ICookie {
  name?: string;
  value?: any;
  path?: string;
  domain?: string;
  expires?: string;
  secure?: boolean;
  sameSite?: string;
}

const getCookieValueByKey = (cookie: string, key: string): string => {
  const indexableKeys: string[] = ['path', 'domain', 'expires'];
  const validKeys: string[] = ['name', 'value', ...indexableKeys];

  const isValidKey = validKeys.some(vKey => vKey === key);
  const isIndexableKey = indexableKeys.some(iKey => iKey === key);

  const cleanCookie: string = cookie && cookie.split(' ').join('');

  if (!cookie || !isValidKey) {
    return '';
  }

  if (key === 'name' || key === 'value') {
    const name = cleanCookie.split('=')[0];

    if (key === 'value') {
      return cleanCookie.substr(name.length + 1);
    }

    return name;
  }

  return cleanCookie.split(`${key}=`)[1]
    ? cleanCookie.split(`${key}=`)[1].split(';')[0]
    : '';
};

export default class Cookie implements ICookie {
  name?: string = '';
  value?: any = '';
  path?: string = '/';
  domain?: string = getDomain();
  secure?: boolean = process.env.VUE_APP_DOMAIN !== 'localhost';
  expires?: string = '';
  cookie?: string = '';
  sameSite?: string = 'Strict';

  constructor(cookie: ICookie) {
    this.name = cookie.name ? cookie.name : this.name;
    this.value = cookie.value ? cookie.value : this.value;
    this.path = cookie.path ? cookie.path : this.path;
    this.domain = cookie.domain ? cookie.domain : this.domain;
    this.expires = cookie.expires ? cookie.expires : this.expires;
    this.secure = cookie.secure ? cookie.secure : this.secure;
    this.sameSite = cookie.sameSite ? cookie.sameSite : this.sameSite;

    this.updateCookie(this.getSession());

    return this.buildCookie();
  }

  private treatCookie(cookie: any): ICookie {
    const name = getCookieValueByKey(cookie, 'name');
    const value = getCookieValueByKey(cookie, 'value');
    const path = getCookieValueByKey(cookie, 'path');
    const domain = getCookieValueByKey(cookie, 'domain');
    const expires = getCookieValueByKey(cookie, 'expires');
    const samesite = getCookieValueByKey(cookie, 'samesite');

    const treatedCookie = {
      ...name && { name },
      ...value && { value },
      ...path && { path },
      ...domain && { domain },
      ...expires && { expires },
      ...samesite && { samesite },
    };

    return treatedCookie;
  }

  private buildCookie(): Cookie {
    const cookiePath = this.path ? `path=${this.path};` : '';
    const cookieDomain = this.domain ? `domain=${this.domain};` : '';
    const cookieExpires = this.expires ? `expires=${this.expires};` : '';
    const cookieSecure = this.secure ? 'secure;' : '';
    const cookieSameSite = this.sameSite ? `SameSite=${this.sameSite}` : '';

    this.cookie = `${this.name}=${this.value};${cookiePath}${cookieDomain}${cookieExpires}${cookieSecure}${cookieSameSite}`;

    return this;
  }

  public updateCookie(cookie: ICookie): Cookie {
    cookie.value && this.updateValue(cookie.value as string);
    cookie.path && this.updatePath(cookie.path as string);
    cookie.domain && this.updateDomain(cookie.domain as string);
    cookie.expires && this.updateExpires(cookie.expires as string);

    return this;
  }

  public updateValue(value: any): Cookie {
    this.value = value;
    this.buildCookie();

    return this;
  }

  public updatePath(path: string): Cookie {
    this.path = path;
    this.buildCookie();

    return this;
  }

  public updateDomain(domain: string): Cookie {
    this.domain = domain;
    this.buildCookie();

    return this;
  }

  public updateExpires(expires: string): Cookie {
    this.expires = expires;
    this.buildCookie();

    return this;
  }

  public setCookie(): Cookie {
    document.cookie = this.cookie as string;

    return this;
  }

  public getSession(): any {
    const cookie = document.cookie.split(';').find((cookie: string) => {
      return this.treatCookie(cookie).name === this.name;
    });

    return this.treatCookie(cookie);
  }

  public deleteCookie(): Cookie {
    this.updateExpires('Thu, 01 Jan 1970 00:00:01 GMT');

    this.buildCookie();
    this.setCookie();

    Object.assign(this, new Cookie({ name: this.name }));

    return this;
  }
}
